// The order on a conversation, in one compact line (owner, 2026-10-30):
// `#1249` · [status chip]² · [Payé]. The small number sits on the chip's
// corner when she ordered more than once; a tap on it (or a hover, desktop)
// shows her other orders. Rules: orderBadge.ts.
import { useEffect, useRef, useState } from 'react';
import { useT } from '@/lib/i18n';
import type { ConversationOrders } from '@/lib/api';
import { othersTitle, rowBadges, type Chip, type RowBadges } from './orderBadge';

const chipCls = 'inline-flex items-center shrink-0 h-[18px] px-1.5 rounded-full border text-[10px] font-semibold leading-none whitespace-nowrap';

export function ChipPill({ chip, className = '' }: { chip: Chip; className?: string }) {
  const { t } = useT();
  return <span className={`${chipCls} ${chip.cls} ${className}`}>{t(chip.label)}</span>;
}

/** The other orders, one per line. */
function OthersList({ b }: { b: RowBadges }) {
  const { t } = useT();
  return (
    <ul className="space-y-1" data-other-orders>
      {b.others.map((o, i) => (
        <li key={`${o.tag}-${i}`} className="flex items-center gap-1.5 text-[11px] text-gray-600 tabular-nums">
          <span className="font-medium text-gray-700">{o.tag}</span>
          {o.status && <ChipPill chip={o.status} />}
          {o.paid && <ChipPill chip={o.paid} />}
        </li>
      ))}
      {!b.others.length && <li className="text-[11px] text-gray-500">{t('Une seule commande.')}</li>}
    </ul>
  );
}

/**
 * `reveal`:
 *   · 'inline'  — the list row: the other orders open UNDER the chips, inside
 *                 the card (the list scrolls; a floating box would be clipped).
 *   · 'popover' — the chat header: a small box under the count.
 */
export function OrderChips({ orders, reveal = 'inline', className = '' }: {
  orders: ConversationOrders | null | undefined; reveal?: 'inline' | 'popover'; className?: string;
}) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement | null>(null);
  const b = rowBadges(orders);
  const key = orders?.current?.name ?? '';
  useEffect(() => { setOpen(false); }, [key]);
  useEffect(() => {
    if (!open || reveal !== 'popover') return;
    const away = (e: PointerEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [open, reveal]);
  if (!b) return null;
  const more = b.count > 1;
  return (
    <div ref={box} className={`relative min-w-0 ${className}`} data-order-chips>
      <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
        <span className="shrink-0 text-[11px] font-semibold text-gray-700 tabular-nums" data-order-tag>{b.tag}</span>
        {b.status && (
          <span className="relative inline-flex shrink-0">
            <ChipPill chip={b.status} />
            {more && (
              <button type="button" data-order-count aria-expanded={open}
                aria-label={t('{n} commandes — voir les autres', { n: b.count })}
                title={othersTitle(b, t)}
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen((v) => !v); }}
                className="absolute -top-2 -right-2 h-4 min-w-4 px-1 rounded-full bg-brand-600 text-white text-[9.5px] font-bold leading-4 text-center tabular-nums shadow-sm cursor-pointer ring-2 ring-white dark:ring-transparent focus:outline-none focus-visible:ring-brand-300 before:absolute before:-inset-2 before:content-['']">
                {b.count}
              </button>
            )}
          </span>
        )}
        {b.paid && <ChipPill chip={b.paid} className={more && b.status ? 'ml-1.5' : ''} />}
      </div>
      {open && more && (reveal === 'inline' ? (
        <div className="mt-1.5 pl-0.5" onClick={(e) => e.stopPropagation()}><OthersList b={b} /></div>
      ) : (
        <div role="group" aria-label={t('Ses autres commandes')} onClick={(e) => e.stopPropagation()}
          className="absolute right-0 top-full mt-2 z-30 min-w-44 rounded-xl border border-gray-200 bg-white shadow-lg p-2.5 storiz-fade-in">
          <p className="text-[10.5px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5">{t('Ses autres commandes')}</p>
          <OthersList b={b} />
        </div>
      ))}
    </div>
  );
}
